/**
 * Analytics Service for Google Analytics 4 Custom Events
 * Centralized tracking for IBRD application events
 */

class AnalyticsService {
  constructor() {
  this.debugMode = process.env.NODE_ENV === 'development';
  // Simple in‑memory queue for events fired before gtag loads
  this._pendingEvents = [];
  // Poll for gtag availability if not yet present (async script)
  this._gtagReadyCheckAttempts = 0;
    
    // Get the dynamic tracking ID set in index.html
    this.measurementId = typeof window !== 'undefined' && window.GA_MEASUREMENT_ID
      ? window.GA_MEASUREMENT_ID
      : 'G-9ML47LVCBX'; // fallback
    
    // Determine environment based on hostname
    this.environment = typeof window !== 'undefined'
      ? (window.location.hostname === '406registration.com' ? 'production' : 'staging/development')
      : 'unknown';
    
    // Debug: Test if gtag is available
    this._logInit();
    this._scheduleFlushIfNeeded();
  }

  /** Derived gtag availability (do NOT cache; script loads async) */
  get isGtagAvailable() {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  _logInit() {
    if (!this.debugMode) return;
    console.log('AnalyticsService initialized:', {
      gtagAvailable: this.isGtagAvailable,
      debugMode: this.debugMode,
      measurementId: this.measurementId,
      environment: this.environment,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
    });
  }

  _scheduleFlushIfNeeded() {
    if (this.isGtagAvailable) {
      this._flushPending();
      return;
    }
    if (this._gtagReadyCheckAttempts > 40) { // ~20s max (40 * 500ms)
      if (this.debugMode && this._pendingEvents.length) {
        console.warn('gtag not available after retries; dropping queued analytics events', this._pendingEvents);
      }
      return;
    }
    setTimeout(() => {
      this._gtagReadyCheckAttempts += 1;
      this._scheduleFlushIfNeeded();
    }, 500);
  }

  _flushPending() {
    if (!this._pendingEvents.length || !this.isGtagAvailable) return;
    if (this.debugMode) console.log(`Flushing ${this._pendingEvents.length} queued analytics events`);
    this._pendingEvents.forEach(({ name, params }) => {
      try { window.gtag('event', name, params); } catch (e) { console.warn('Failed to flush analytics event', name, e); }
    });
    this._pendingEvents = [];
  }

  /**
   * Generic event tracking method
   * @param {string} eventName - Name of the event
   * @param {Object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    try {
      // Add common parameters to all events
      const enrichedParameters = {
        ...parameters,
        app_name: 'IBRD',
        app_version: this._getAppVersion(),
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_title: document.title,
        environment: this.environment,
        measurement_id: this.measurementId,
        // In GA4 you can force events to appear in DebugView with debug_mode parameter (non-prod only)
        ...(this.environment !== 'production' ? { debug_mode: true } : {})
      };

      if (this.debugMode) console.log('Analytics Event prepared:', eventName, enrichedParameters);

      if (!this.isGtagAvailable) {
        // queue until gtag available
        this._pendingEvents.push({ name: eventName, params: enrichedParameters });
        this._scheduleFlushIfNeeded();
        return;
      }

      window.gtag('event', eventName, enrichedParameters);
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }

  /**
   * Track beacon certificate printing
   * @param {Object} beaconData - Beacon information
   * @param {Object} userData - User information
   * @param {string} certificateType - Type of certificate (brief/detailed)
   */
  trackCertificatePrint(beaconData, userData, certificateType = 'detailed') {
    this.trackEvent('cert_print', {
      event_category: 'beacon_operations',
      event_label: 'cert_print',
      beacon_hex_id: beaconData.hexId || 'unknown',
      beacon_type: beaconData.beaconType || 'unknown',
      beacon_country_code: beaconData.beaconCountryCode || 'unknown',
      beacon_serial_number: beaconData.serialNumber || 'unknown',
      beacon_manufacturer: beaconData.beaconManufacturer || 'unknown',
      beacon_special_status: beaconData.specialStatus || 'ACTIVE',
      owner_name: beaconData.ownerName || 'unknown',
      user_id: userData._id || 'unknown',
      user_email: userData.username || 'unknown',
      user_role: userData.roleId || 'unknown',
      certificate_type: certificateType,
      print_method: 'web_interface',
      value: 1
    });
  }

  /**
   * Track beacon search operations
   * @param {Object} searchParams - Search parameters used
   * @param {number} resultsCount - Number of results returned
   * @param {Object} userData - User information
   */
  trackBeaconSearch(searchParams, resultsCount, userData) {
    this.trackEvent('beacon_searched', {
      event_category: 'beacon_operations',
      event_label: 'search_beacons',
      search_type: this._getSearchType(searchParams),
      search_params: JSON.stringify(searchParams),
      results_count: resultsCount,
      user_id: userData._id || 'unknown',
      user_role: userData.roleId || 'unknown',
      value: resultsCount
    });
  }

  /**
   * Track beacon registration/edit operations
   * @param {string} operation - 'create' or 'update'
   * @param {Object} beaconData - Beacon information
   * @param {Object} userData - User information
   */
  trackBeaconOperation(operation, beaconData, userData) {
    this.trackEvent(`beacon_${operation}`, {
      event_category: 'beacon_operations',
      event_label: `${operation}_beacon`,
      beacon_type: beaconData.beaconType || 'unknown',
      beacon_country_code: beaconData.beaconCountryCode || 'unknown',
      user_id: userData._id || 'unknown',
      user_role: userData.roleId || 'unknown',
      operation_type: operation,
      value: 1
    });
  }

  /**
   * Track user login events
   * @param {Object} userData - User information
   * @param {string} loginMethod - Method used to login
   */
  trackUserLogin(userData, loginMethod = 'standard') {
    this.trackEvent('user_login', {
      event_category: 'user_authentication',
      event_label: 'login_success',
      user_id: userData._id || 'unknown',
      user_role: userData.roleId || 'unknown',
      login_method: loginMethod,
      value: 1
    });
  }

  /**
   * Track page views with custom parameters
   * @param {string} pageName - Name of the page
   * @param {Object} additionalParams - Additional parameters
   */
  trackPageView(pageName, additionalParams = {}) {
    this.trackEvent('page_view', {
      event_category: 'navigation',
      event_label: pageName,
      page_name: pageName,
      ...additionalParams
    });
  }

  /**
   * Track errors and exceptions
   * @param {Error} error - Error object
   * @param {string} context - Context where error occurred
   */
  trackError(error, context) {
    this.trackEvent('exception', {
      event_category: 'errors',
      event_label: context,
      error_message: error.message,
      error_stack: error.stack,
      context: context,
      fatal: false
    });
  }

  /**
   * Helper method to determine search type
   * @private
   */
  _getSearchType(searchParams) {
    if (searchParams.hexId) return 'hex_id';
    if (searchParams.MMSI) return 'mmsi';
    if (searchParams.vehicleRegistrationNumber) return 'registration';
    if (searchParams.ownerName) return 'owner_name';
    if (searchParams.callSign) return 'call_sign';
    return 'general';
  }

  /**
   * Set user properties for better segmentation
   * @param {Object} userData - User information
   */
  setUserProperties(userData) {
    try {
      const props = {
        user_id: userData._id,
        user_role: userData.roleId,
        user_email: userData.username,
        registration_date: userData.createdDate
      };
      if (!this.isGtagAvailable) {
        // queue as synthetic event; will flush once gtag ready
        this._pendingEvents.push({ name: 'user_properties_pending', params: props });
        this._scheduleFlushIfNeeded();
        return;
      }
      // Proper GA4 way to set user properties
      window.gtag('set', 'user_properties', props);
      if (this.debugMode) console.log('GA4 user properties set', props);
    } catch (error) {
      console.error('User properties setting error:', error);
    }
  }

  _getAppVersion() {
    try {
      // Attempt to read version stamped on global (if you've injected it) or from package.json via env
      if (typeof window !== 'undefined' && window.APP_VERSION) return window.APP_VERSION;
      if (process.env.REACT_APP_VERSION) return process.env.REACT_APP_VERSION;
    } catch (_) {}
    return '1.0.0';
  }

  /**
   * Test method - Fire a test event to verify tracking works
   * Call this from browser console: analyticsService.fireTestEvent()
   */
  fireTestEvent() {
    console.log('Firing test certificate_printed event...');
    this.trackEvent('certificate_printed', {
      event_category: 'test',
      event_label: 'test_certificate',
      beacon_hex_id: 'TEST123456789ABC',
      beacon_type: 'PLB',
      beacon_country_code: '366',
      user_role: 'test_user',
      certificate_type: 'detailed',
      value: 1
    });
    console.log('Test event fired! Check GA4 Real-time reports in 1-2 minutes.');
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Make available globally for testing (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.analyticsService = analyticsService;
}

export default analyticsService;

// Named exports for specific tracking functions
export const {
  trackEvent,
  trackCertificatePrint,
  trackBeaconSearch,
  trackBeaconOperation,
  trackUserLogin,
  trackPageView,
  trackError,
  setUserProperties
} = analyticsService;